/**
 * AuthContext - Centralised Authentication State Management
 *
 * WHY THIS ARCHITECTURE:
 * We use a lightweight Context for auth state rather than React Query because:
 * 1. Auth state changes trigger app-wide re-renders (nav, protected routes)
 * 2. User object is accessed synchronously throughout the component tree
 * 3. Prevents prop drilling across deeply nested authentication components
 *
 * SECURITY RATIONALE:
 * - Minimal surface area: Only stores user object, not sensitive tokens
 * - Tokens stored in localStorage for XSS mitigation (httpOnly cookies not viable with backend proxy)
 * - User object validated server-side on each protected API call
 *
 * INTEGRATION PATTERN:
 * Works alongside React Query which handles API state and caching.
 * This context is for UI state only - actual auth verification happens via useSession hook.
 *
 * @see useSession - Verifies token validity with AWS Cognito
 * @see apiAuth.js - Token management and storage layer
 */
import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from 'react';

/**
 * AWS Cognito user object interface
 *
 * WHY flexible structure:
 * - Cognito user attributes vary by configuration
 * - Custom attributes can be added via Cognito console
 * - Standard attributes documented below for reference
 *
 * Standard Cognito attributes (all optional):
 * - email, phone_number, name, given_name, family_name
 * - birthdate, gender, locale, address
 * - custom attributes prefixed with "custom:"
 */
export interface CognitoUser {
  /** Cognito user sub (UUID) - stable identifier */
  sub: string;

  /** User's email address */
  email?: string;

  /** Email verified flag */
  email_verified?: boolean;

  /** User's full name */
  name?: string;

  /** User's phone number (E.164 format) */
  phone_number?: string;

  /** Phone verified flag */
  phone_number_verified?: boolean;

  /** Cognito user groups/roles */
  'cognito:groups'?: string[];

  /** User's preferred username */
  preferred_username?: string;

  /** Account created timestamp (Unix epoch) */
  created_at?: number;

  /** Last modified timestamp (Unix epoch) */
  updated_at?: number;

  /** MFA enabled flag */
  mfa_enabled?: boolean;

  /** Custom attributes */
  [key: string]: unknown;
}

/**
 * Auth context value interface
 */
export interface AuthContextValue {
  /** Current authenticated user or null if not logged in */
  user: CognitoUser | null;

  /**
   * Update user state
   *
   * WHY Dispatch<SetStateAction<T>>:
   * - Matches React.useState signature exactly
   * - Allows both direct value and updater function
   * - Type-safe with TypeScript
   *
   * @example
   * // Direct value
   * setUser(newUser);
   *
   * @example
   * // Updater function
   * setUser(prevUser => ({ ...prevUser, name: 'New Name' }));
   */
  setUser: Dispatch<SetStateAction<CognitoUser | null>>;
}

/**
 * Create context with undefined default
 *
 * WHY undefined instead of throwing:
 * - Allows context to exist before provider mounts
 * - Custom hook handles error boundary
 * - More flexible for testing
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthContextProvider props interface
 */
export interface AuthContextProviderProps {
  /** App component tree */
  children: ReactNode;
}

/**
 * AuthContextProvider - Root-level authentication state provider
 *
 * WHY THIS DESIGN:
 * - Single source of truth for user authentication state across the app
 * - Triggers re-renders only when user object changes (login/logout)
 * - Lightweight compared to Redux/Zustand for simple auth state
 *
 * PLACEMENT RATIONALE:
 * Must wrap entire app (in App.jsx) to ensure:
 * 1. Protected routes can access auth state
 * 2. Navigation components can show/hide based on auth
 * 3. Feature modules can check permissions
 *
 * STATE MANAGEMENT:
 * User object is set by:
 * - useLogin hook on successful authentication
 * - useSession hook on app mount (token validation)
 * - useLogout hook to clear on sign out
 *
 * @param props - Provider props
 */
export function AuthContextProvider({ children }: AuthContextProviderProps): JSX.Element {
  // WHY: useState instead of useReducer - Simple boolean state doesn't need complex state logic
  // User object structure determined by AWS Cognito response format
  const [user, setUser] = useState<CognitoUser | null>(null);

  const value: AuthContextValue = {
    user,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuthorizedUser - Hook to access authentication context
 *
 * WHY THIS PATTERN:
 * - Custom hook enforces correct usage within provider boundary
 * - Throws descriptive error if used outside provider (common mistake)
 * - Consistent naming convention with other auth hooks
 *
 * ERROR BOUNDARY:
 * The error throw is intentional - it's a development-time error that
 * indicates incorrect component tree structure, not a runtime auth failure.
 *
 * USAGE:
 * Components should prefer specific auth hooks (useLogin, useLogout) over
 * directly accessing this context. This hook is for components that need
 * to read user state for conditional rendering.
 *
 * @returns Auth context value containing user and setUser
 * @throws {Error} If used outside of AuthContextProvider
 *
 * @example
 * // Read user state for conditional rendering
 * const { user } = useAuthorizedUser();
 * if (!user) return <LoginPrompt />;
 *
 * @example
 * // Update user state after profile edit
 * const { setUser } = useAuthorizedUser();
 * setUser({ ...user, name: 'New Name' });
 */
export function useAuthorizedUser(): AuthContextValue {
  const context = useContext(AuthContext);

  // WHY: Explicit undefined check rather than falsy check
  // Allows null as valid state (user logged out) while catching missing provider
  if (context === undefined) {
    throw new Error('useAuthorizedUser must be used within AuthContextProvider');
  }

  return context;
}
